import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { Draggable } from '@neodrag/vanilla';
import { ChatHeader } from './ChatHeader';
import { ChatMessages } from './ChatMessages';
import { ChatInput } from './ChatInput';
// Percentage-based defaults for expanded state
const DEFAULT_WIDTH_PCT = 0.32;
const DEFAULT_HEIGHT_PCT = 0.83;
const MIN_WIDTH = 340;
const MIN_HEIGHT = 400;
const MAX_WIDTH_PCT = 0.60;
const MAX_HEIGHT_PCT = 0.92;
// Minimized stays pixel-based (small fixed pill)
const MINIMIZED_WIDTH_PX = 320;
const MINIMIZED_HEIGHT_PX = 56;
const EDGE_MARGIN = 24;
const NAV_WIDTH = 256;
function clampSize(width, height) {
    const maxW = typeof window !== 'undefined' ? window.innerWidth * MAX_WIDTH_PCT : 1200;
    const maxH = typeof window !== 'undefined' ? window.innerHeight * MAX_HEIGHT_PCT : 900;
    return {
        width: Math.max(MIN_WIDTH, Math.min(width, maxW)),
        height: Math.max(MIN_HEIGHT, Math.min(height, maxH)),
    };
}
function getChatPosition(isMinimized, snapPosition, chatSize, positionOverride) {
    if (typeof window === 'undefined')
        return { x: 0, y: 0 };
    const { innerHeight, innerWidth } = window;
    // On mobile, center the chat
    if (innerWidth < 768)
        return { x: EDGE_MARGIN, y: 64 };
    // Use override position if set (from edge resize)
    if (positionOverride)
        return positionOverride;
    const chatHeight = isMinimized ? MINIMIZED_HEIGHT_PX : chatSize.height;
    if (snapPosition === 'left') {
        return {
            x: NAV_WIDTH + EDGE_MARGIN,
            y: innerHeight - chatHeight - EDGE_MARGIN,
        };
    }
    // Right / default
    const chatWidth = isMinimized ? MINIMIZED_WIDTH_PX : chatSize.width;
    return {
        x: innerWidth - chatWidth - EDGE_MARGIN,
        y: innerHeight - chatHeight - EDGE_MARGIN,
    };
}
export const AIChatWindow = ({ state, onClose, onMinimize, onExpand, onSendMessage, onSelectVariation, onSelectAllVariations, onExecuteAction, onProvideFeedback, onMessageFeedback, }) => {
    const { isOpen, isMinimized, messages, context, isLoading, error, suggestClose, rating, approachingTurnLimit, remainingTurns } = state;
    const nodeRef = useRef(null);
    // Dynamic size state — initialized from viewport percentages
    const [size, setSize] = useState(() => {
        if (typeof window === 'undefined')
            return { width: 512, height: 860 };
        return clampSize(Math.round(window.innerWidth * DEFAULT_WIDTH_PCT), Math.round(window.innerHeight * DEFAULT_HEIGHT_PCT));
    });
    const hasUserResizedRef = useRef(false);
    const positionOverrideRef = useRef(null);
    // Use a ref for snap position so it's always current when the effect reads it,
    // avoiding batching issues between local state and signal-driven re-renders
    const snapPositionRef = useRef(null);
    const [positionVersion, setPositionVersion] = useState(0);
    // Create Draggable instance — destroy and recreate when position-affecting state changes
    useEffect(() => {
        if (!nodeRef.current || !isOpen)
            return;
        const pos = getChatPosition(isMinimized, snapPositionRef.current, size, positionOverrideRef.current);
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
        // Clear position override after it's been consumed
        positionOverrideRef.current = null;
        const d = new Draggable(nodeRef.current, {
            handle: '.drag-handle',
            defaultPosition: pos,
            disabled: isMobile,
        });
        return () => d.destroy();
    }, [isOpen, isMinimized, positionVersion]);
    // Recalculate size on window resize
    useEffect(() => {
        const onWindowResize = () => {
            if (!hasUserResizedRef.current) {
                setSize(clampSize(Math.round(window.innerWidth * DEFAULT_WIDTH_PCT), Math.round(window.innerHeight * DEFAULT_HEIGHT_PCT)));
            }
            else {
                setSize(prev => clampSize(prev.width, prev.height));
            }
        };
        window.addEventListener('resize', onWindowResize);
        return () => window.removeEventListener('resize', onWindowResize);
    }, []);
    // Resize handler via native pointer events
    const handleResizePointerDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const target = e.currentTarget;
        const edges = target.dataset.edges || '';
        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = size.width;
        const startHeight = size.height;
        const node = nodeRef.current;
        if (!node)
            return;
        // Parse position from neodrag's translate property (format: "Xpx Ypx")
        const translateStr = node.style.translate || '0px 0px';
        const parts = translateStr.split(' ');
        const startPosX = parseFloat(parts[0]) || 0;
        const startPosY = parseFloat(parts[1]) || 0;
        let currentPosX = startPosX;
        let currentPosY = startPosY;
        const onPointerMove = (ev) => {
            const dx = ev.clientX - startX;
            const dy = ev.clientY - startY;
            let newWidth = startWidth;
            let newHeight = startHeight;
            let newPosX = startPosX;
            let newPosY = startPosY;
            if (edges.includes('right'))
                newWidth = startWidth + dx;
            if (edges.includes('left')) {
                newWidth = startWidth - dx;
                newPosX = startPosX + dx;
            }
            if (edges.includes('bottom'))
                newHeight = startHeight + dy;
            if (edges.includes('top')) {
                newHeight = startHeight - dy;
                newPosY = startPosY + dy;
            }
            const clamped = clampSize(newWidth, newHeight);
            // Adjust position for left/top edges so opposite edge stays anchored
            if (edges.includes('left')) {
                newPosX = startPosX + (startWidth - clamped.width);
            }
            if (edges.includes('top')) {
                newPosY = startPosY + (startHeight - clamped.height);
            }
            // Update via translate property (same as neodrag uses) to avoid conflicts
            if (edges.includes('left') || edges.includes('top')) {
                node.style.translate = `${newPosX}px ${newPosY}px`;
                currentPosX = newPosX;
                currentPosY = newPosY;
            }
            setSize(clamped);
        };
        const onPointerUp = () => {
            hasUserResizedRef.current = true;
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
            // If position changed (left/top resize), recreate Draggable at new position
            if (edges.includes('left') || edges.includes('top')) {
                positionOverrideRef.current = { x: currentPosX, y: currentPosY };
                snapPositionRef.current = null;
                setPositionVersion(v => v + 1);
            }
        };
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
    }, [size]);
    const snapToRight = useCallback(() => {
        snapPositionRef.current = 'right';
        setPositionVersion(v => v + 1);
    }, []);
    const snapToLeft = useCallback(() => {
        snapPositionRef.current = 'left';
        setPositionVersion(v => v + 1);
    }, []);
    const handleMinimize = useCallback(() => {
        snapPositionRef.current = 'right';
        setPositionVersion(v => v + 1);
        onMinimize();
    }, [onMinimize]);
    const handleExpand = useCallback(() => {
        snapPositionRef.current = 'right';
        setPositionVersion(v => v + 1);
        onExpand();
    }, [onExpand]);
    if (!isOpen)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden", onClick: onClose }), _jsxs("div", { ref: nodeRef, className: `fixed bg-white dark:bg-slate-900 rounded-xl shadow-2xl z-[60]
                   ${isMinimized ? 'w-80 h-14' : ''}
                   max-md:!w-[calc(100vw-3rem)]
                   max-md:!h-[calc(100vh-8rem)]
                   flex flex-col overflow-hidden`, style: {
                    left: 0,
                    top: 0,
                    ...(isMinimized ? {} : { width: `${size.width}px`, height: `${size.height}px` }),
                }, children: [_jsx(ChatHeader, { context: context, onClose: onClose, onMinimize: handleMinimize, onExpand: handleExpand, isMinimized: isMinimized, onSnapLeft: typeof window !== 'undefined' && window.innerWidth >= 768 ? snapToLeft : undefined, onSnapRight: typeof window !== 'undefined' && window.innerWidth >= 768 ? snapToRight : undefined }), !isMinimized && (_jsxs(_Fragment, { children: [error && (_jsx("div", { className: "px-4 py-2 bg-red-50 border-b border-red-200", children: _jsxs("div", { className: "flex items-center gap-2 text-sm text-red-700", children: [_jsx("svg", { className: "w-4 h-4 flex-shrink-0", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" }) }), _jsx("span", { children: error })] }) })), _jsx(ChatMessages, { messages: messages, isLoading: isLoading, onSelectVariation: onSelectVariation, onSelectAllVariations: onSelectAllVariations, onExecuteAction: onExecuteAction, availableFields: context?.availableFields || [], suggestClose: suggestClose || (approachingTurnLimit && remainingTurns === 0), currentRating: rating, onProvideFeedback: onProvideFeedback, onMessageFeedback: onMessageFeedback }), _jsx(ChatInput, { onSend: onSendMessage, disabled: isLoading || !!error || (approachingTurnLimit && remainingTurns === 0), placeholder: context?.viewMode === 'Edit'
                                    ? 'Ask me to help with your content...'
                                    : 'Ask me anything...', approachingTurnLimit: approachingTurnLimit, remainingTurns: remainingTurns }), _jsx("div", { "data-edges": "right", onPointerDown: handleResizePointerDown, className: "absolute top-0 right-0 w-1.5 h-full cursor-ew-resize hidden md:block" }), _jsx("div", { "data-edges": "left", onPointerDown: handleResizePointerDown, className: "absolute top-0 left-0 w-1.5 h-full cursor-ew-resize hidden md:block" }), _jsx("div", { "data-edges": "bottom", onPointerDown: handleResizePointerDown, className: "absolute bottom-0 left-0 h-1.5 w-full cursor-ns-resize hidden md:block" }), _jsx("div", { "data-edges": "top", onPointerDown: handleResizePointerDown, className: "absolute top-0 left-0 h-1.5 w-full cursor-ns-resize hidden md:block" }), _jsx("div", { "data-edges": "bottom-right", onPointerDown: handleResizePointerDown, className: "absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize hidden md:block" }), _jsx("div", { "data-edges": "bottom-left", onPointerDown: handleResizePointerDown, className: "absolute bottom-0 left-0 w-3 h-3 cursor-nesw-resize hidden md:block" }), _jsx("div", { "data-edges": "top-right", onPointerDown: handleResizePointerDown, className: "absolute top-0 right-0 w-3 h-3 cursor-nesw-resize hidden md:block" }), _jsx("div", { "data-edges": "top-left", onPointerDown: handleResizePointerDown, className: "absolute top-0 left-0 w-3 h-3 cursor-nwse-resize hidden md:block" })] }))] })] }));
};
